import { Box } from '@zillow/constellation-10';

import { LoadingHeading, LoadingImage, LoadingParagraph, LoadingText } from '@zg-rentals/loading-components';

import {
  getFormattedAddress,
  getPhotoThumbUrl,
  getPropertyTypeLabel,
  getUnitSummary,
} from '../../../utils/flexiblePropertyCardUtils';
import { PropertyPhoto } from '../../../components/PropertyPhoto';

import {
  headerStyles,
  infoBlockStyles,
  photoContainerStyles,
  truncatedTextStyles,
} from '../../../styles/propertyCardTable';

import type { AccountViewListingGroupDto } from '@zg-rentals/ts-rental-manager-api-client';

type Props = {
  listing: AccountViewListingGroupDto;
  isLoading?: boolean;
};

const FlexiblePropertyCardHeader = ({ listing, isLoading = false }: Props) => {
  const propertyTypeLabel = getPropertyTypeLabel(listing);
  const formattedAddress = getFormattedAddress(listing);
  const unitSummary = getUnitSummary(listing);
  const photoUrl = getPhotoThumbUrl(listing.photoKey);

  return (
    <Box id="ft-flexible-property-card-header" css={headerStyles} data-testid="FlexiblePropertyCard-Header">
      <Box css={photoContainerStyles} data-loading={isLoading || null}>
        {isLoading ? (
          <LoadingImage isLoading={true} loadingWidth="100%" loadingHeight="100%" />
        ) : (
          <PropertyPhoto src={photoUrl} alt={formattedAddress} />
        )}
      </Box>

      <Box css={infoBlockStyles}>
        <LoadingText textStyle="body-xs-bold" isLoading={isLoading} loadingWidth="60px" css={{ color: 'text.subtle' }}>
          {propertyTypeLabel}
        </LoadingText>

        <LoadingHeading
          level={2}
          isLoading={isLoading}
          loadingWidth="80%"
          // Longhands rather than `textStyle`: Heading always emits its own level-based textStyle class, which only longhand atomics outrank.
          css={{
            ...truncatedTextStyles,
            fontSize: 'body',
            fontFamily: 'default',
            fontWeight: 'emphasis',
            lineHeight: 'body',
          }}
        >
          {formattedAddress}
        </LoadingHeading>

        <LoadingParagraph textStyle="body-sm" isLoading={isLoading} loadingWidth="120px" css={{ color: 'text.subtle' }}>
          {unitSummary}
        </LoadingParagraph>
      </Box>
    </Box>
  );
};

export default FlexiblePropertyCardHeader;
